import { LitElement } from 'lit';

/**
 * Handles showing, hiding, draggging, and reinitializing the annotation panel
 * and its container object. It should be a sibling of that container.
 *
 * @listens closeAnnotationPanel
 * @listens dragstart, dragover, drop
 * @listens eplib-clear-annotations
 * @listens eplib-login-completed
 * @listens eplib-logout
 * @listens eplib-set-annotations
 * @listens eplib-set-hide-annotations
 * @listens wordsSelected
 */
export class EplibAnnotationPanelHelper extends LitElement {

  static get properties() {
    return {
      ...super.properties,
      annotationPanelContainer: {
        type: HTMLElement
      },
      annotationPanel: {
        type: HTMLElement
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.annotationPanel = this.parentElement.querySelector("#annotation-panel");
    this.annotationPanelContainer = this.parentElement.querySelector("#annotations");

    this.annotationPanelContainer.addEventListener("dragstart", event => this.annopanelDragStart(event));
    window.addEventListener('eplib-set-hide-annotations', event => this.setHideAnnotations(event, this));

    // This is a custom event that will be fired by the close box on the panel.
    window.addEventListener('closeAnnotationPanel', event => this.hideAnnotationPanelContainer(event, this));

    window.addEventListener('wordsSelected', event => this.showAnnotationPanelContainer(event, this));
    window.addEventListener('eplib-clear-annotations', event => this.clearAnnotations(event, this));
    window.addEventListener('eplib-set-annotations', event => this.setAnnotations(event, this));

    // hide annotation panel
    this.hideAnnotationPanelContainer(null, this);

    const contentDiv = document.getElementById("content");

    if (contentDiv) {
        contentDiv.addEventListener("dragover", event => this.contentDragOver(event));
        contentDiv.addEventListener("drop", event => this.contentDrop(event, this));
    }

    window.addEventListener('eplib-login-completed', event => this.resetPanel(event, this));
    window.addEventListener('eplib-logout', event => this.resetPanel(event, this));
  }

  disconnectedCallback() {
    window.removeEventListener('eplib-logout', event => this.resetPanel(event, this));
    window.removeEventListener('eplib-login-completed', event => this.resetPanel(event, this));

    const contentDiv = document.getElementById("content");

    if (contentDiv) {
        contentDiv.removeEventListener("dragover", event => this.contentDragOver(event));
        contentDiv.removeEventListener("drop", event => this.contentDrop(event, this));
    }

    window.removeEventListener('eplib-set-annotations', event => this.setAnnotations(event, this));
    window.removeEventListener('eplib-clear-annotations', event => this.clearAnnotations(event, this));
    window.removeEventListener('wordsSelected', this.showAnnotationPanelContainer, true);
    window.removeEventListener('closeAnnotationPanel', event => this.hideAnnotationPanelContainer(event, this));
    window.removeEventListener('eplib-set-hide-annotations', event => this.setHideAnnotations(event, this));
    this.annotationPanelContainer.removeEventListener("dragstart", event => this.annopanelDragStart(event));
  }

  setHideAnnotations(event, that) {
    if (!event.detail.hide_annotations) {
      that.showAnnotationPanelContainer(event, that);
    }
    else {
      that.hideAnnotationPanelContainer(event, that);
    }
  }

  hideAnnotationPanelContainer (event, that) {
    if (!that.annotationPanelContainer) return;
    that.annotationPanelContainer.setAttribute('hidden', true);
   }

  showAnnotationPanelContainer (event, that) {
    if (!that.annotationPanelContainer) return;
    that.annotationPanelContainer.removeAttribute('hidden');
  }

  contentDragOver(event) {
    event.preventDefault();
    return false;
  }

  contentDrop(event, that) {
    const offset = event.dataTransfer.getData("text/plain").split(',');
    that.annotationPanelContainer.style.left = (event.clientX + parseInt(offset[0],10)) + 'px';
    that.annotationPanelContainer.style.top = (event.clientY + parseInt(offset[1],10)) + 'px';
    event.preventDefault();
    return false;
  }

  annopanelDragStart(event) {
    const rect = event.target.getBoundingClientRect();
    event.dataTransfer.setData("text/plain", (rect.left - event.clientX) + ',' + (rect.top - event.clientY));
  }

  setAnnotations(event, that) {
    const annotationData = event.detail.data;
    if (!annotationData) { return }

    const annotationList = that.annotationPanel.shadowRoot.querySelector('annotation-list');
    if (!annotationList) return;

    // remove loading attribute
    annotationList.loading = false;

    // this is to ensure we have an array
    let annotations = [];
    // single entry
    if (typeof annotationData === 'string' && annotationData.length) {
      annotations = [annotationData];
    }
    // multiple entries
    if (typeof annotationData === 'object' && annotationData && annotationData.length) {
      annotations = annotationData;
    }

      // create HTML from strings
    let annotationElements = annotations.map(function (annotationHTML) {
      const template = document.createElement('template');
      template.innerHTML = annotationHTML;
      return template.content.firstChild;
    });

    annotationList.addAnnotations(annotationElements)
  }

  resetPanel(event, that) {
    if (that.annotationPanel
      && that.annotationPanel.reset
      && typeof that.annotationPanel.reset === "function") {
      // reset panel, including clearing the selection
      that.annotationPanel.reset();
    }
  }

  clearAnnotations(event, that) {
    that.resetPanel(event, that);
    if (that.annotationPanel) {
      const annotationList = that.annotationPanel.shadowRoot.querySelector('annotation-list');
      if (!annotationList) return;
      // clear annotations in list
      annotationList.empty();
      // set loading attribute
      annotationList.loading = true;
    }
  }

}

customElements.define('eplib-annotation-panel-helper', EplibAnnotationPanelHelper);
